/**
 * Global Authentication Middleware
 * 
 * Handles authentication verification before protected routes render
 * Prevents the flash of content before redirecting to login for protected routes
 */
import { defineNuxtRouteMiddleware, navigateTo, useRouter } from '#app'
import { useAuth } from '~/composables/useAuth'

export default defineNuxtRouteMiddleware(async (to, from) => {
  // Skip on server-side to prevent hydration issues
  if (process.server) return

  // Get the path from the destination route
  const currentPath = to.path

  // Special handling for /account - login page itself is not protected
  // but all subpaths like /account/dashboard are protected
  const isAccountPage = currentPath === '/account' // The login page itself
  const isVerificationPage = currentPath === '/account/verification' // 2FA verification page
  const isAccountSubpath = currentPath.startsWith('/account/') && !isVerificationPage // Any subpath under account except verification
  
  // Other fully protected routes (including all subpaths)
  const otherProtectedRoutes = [
    '/sim-activation',
    '/checkout'
  ]
  
  // Check if current path is a protected route
  const requiresAuth = isAccountSubpath || otherProtectedRoutes.some(route => 
    currentPath === route || currentPath.startsWith(`${route}/`)
  )

  // Skip auth check for non-protected routes
  if (!requiresAuth) return

  // For protected routes, check auth status immediately
  const auth = useAuth()
  
  // Detect and handle infinite redirect loops
  const isRedirectingFromAccount = from.path === '/account' && to.path.startsWith('/account/')
  if (isRedirectingFromAccount && from.query.returnUrl) {
    console.log(`[Auth Middleware] Detected potential redirect loop for ${to.path}. Allowing navigation.`)
    // Allow the navigation to prevent the redirect loop
    return
  }
  
  // CRITICAL FIX: Ensure session is initialized before any authentication checks
  // This prevents false logout redirects during client-side hydration
  console.log(`[Auth Middleware] Initializing session for protected route '${currentPath}'...`)
  try {
    await auth.getSession()
    console.log(`[Auth Middleware] Session initialized for '${currentPath}', status: ${auth.status.value}`)
  } catch (error) {
    console.error(`[Auth Middleware] Session initialization failed for '${currentPath}':`, error)
  }
  
  // Check if SITEACTIVEID cookie exists - this is the most reliable indicator of being logged in
  // Access the cookie directly rather than from auth composable
  const sessionCookie = useCookie("SITEACTIVEID")
  const hasSiteActiveCookie = !!sessionCookie.value
  
  // Enhanced debug logging to help diagnose authentication issues
  console.log(`[Auth Middleware] Auth check for '${currentPath}': status=${auth.status.value}, user=${!!auth.user.value}, cookie=${hasSiteActiveCookie}`)
  
  // Get the auth status directly from the store
  const { status: authStatus, user: authUser } = auth
  
  // Define routes that require 2FA verification (auth-status)
  const requires2FARoutes = [
    '/account/dashboard?view=personal-details',
    '/account/dashboard?view=orders',
    '/account/dashboard?view=address',
    '/checkout'
  ]
  
  // Define routes that only require basic auth (just logged in, no 2FA needed)
  const basicAuthOnlyRoutes = [
    '/account/dashboard?view=my-plans',
    '/account/dashboard?view=comms-preferences',
    '/account/dashboard?view=saved-cards',
    '/account/dashboard' // Base dashboard without specific view
  ]
  
  // Check if current route requires 2FA
  const requires2FA = requires2FARoutes.some(route => {
    if (route.includes('?')) {
      // For routes with query params, check exact match
      const [path, query] = route.split('?')
      const queryMatch = to.query.view && query.includes(`view=${to.query.view}`)
      return currentPath === path && queryMatch
    }
    return currentPath === route
  })
  
  // Check if current route is explicitly a basic auth only route
  const isBasicAuthOnly = basicAuthOnlyRoutes.some(route => {
    if (route.includes('?')) {
      // For routes with query params, check exact match
      const [path, query] = route.split('?')
      if (!query) return currentPath === path // Handle base dashboard route
      const queryMatch = to.query.view && query.includes(`view=${to.query.view}`)
      return currentPath === path && queryMatch
    }
    return currentPath === route
  })
  
  // For routes that don't require 2FA or are explicitly basic auth only
  if (!requires2FA || isBasicAuthOnly) {
    // User just needs to be logged in (have a valid user object)
    if (authUser.value && authUser.value.id) {
      console.log(`[Auth Middleware] Basic auth check passed for '${currentPath}' - user is logged in`)
      return
    }
  } else {
    // For 2FA-required routes, check authenticated status
    if (authStatus.value === 'authenticated' && authUser.value && authUser.value.id) {
      console.log(`[Auth Middleware] 2FA auth check passed for '${currentPath}' - user is fully authenticated`)
      return
    }
    
    // Check if user needs 2FA (has user but not authenticated status)
    if (authUser.value && authStatus.value !== 'authenticated') {
      console.log(`[Auth Middleware] User needs 2FA verification for '${currentPath}'`)
      // Redirect to verification page instead of login
      return navigateTo({
        path: '/account/verification',
        query: {
          returnUrl: currentPath
        }
      })
    }
  }
  
  // If we reach here, the user is not authenticated
  // Redirect to account page with return URL
  console.log(`[Auth Middleware] Protected route '${currentPath}' requires authentication - redirecting to login`)
  
  // Don't encode the URL here - let navigateTo handle it
  // This prevents double encoding issues during server-side redirects
  return navigateTo({
    path: '/account',
    query: {
      returnUrl: currentPath
    }
  })
})
